import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Alert, Chip,
  CircularProgress, Checkbox, FormControlLabel, Divider,
  useMediaQuery, useTheme, InputAdornment
} from '@mui/material';
import { Add, Sync, ArrowBack, Edit, Delete, Visibility, VisibilityOff, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

function DemVirtualLists() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [databases, setDatabases] = useState([]);
  const [selectedDb, setSelectedDb] = useState(null);
  const [folders, setFolders] = useState([]);
  const [maildomeVLs, setMaildomeVLs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [vlSearch, setVlSearch] = useState('');
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderForm, setFolderForm] = useState({ name: '', selectedVLs: [] });

  useEffect(() => { fetchDatabases(); }, []);
  useEffect(() => { if (selectedDb) { fetchFolders(); fetchMaildomeVLs(); } }, [selectedDb]);

  const fetchDatabases = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/admin/dem/databases');
      setDatabases(res.data.data || []);
    } catch (error) { setMessage({ type: 'error', text: 'Failed to load databases' }); }
    finally { setLoading(false); }
  };

  const fetchFolders = async () => {
    try {
      const res = await axios.get(`/admin/dem/databases/${selectedDb.id}/virtual-lists`);
      setFolders(res.data.data || []);
    } catch (error) { console.error('Error fetching folders:', error); }
  };

  const fetchMaildomeVLs = async () => {
    try {
      const res = await axios.get(`/admin/dem/databases/${selectedDb.id}/maildome-vl`);
      setMaildomeVLs(res.data.data || []);
    } catch (error) { console.error('Error fetching maildome VLs:', error); }
  };

  const handleSync = async () => {
    if (!selectedDb.maildome_db_id) {
      setMessage({ type: 'error', text: 'Maildome DB ID not configured for this database' });
      return;
    }
    setSyncing(true);
    try {
      const res = await axios.post(`/admin/dem/databases/${selectedDb.id}/sync-vl`);
      setMessage({ type: 'success', text: res.data.message });
      fetchMaildomeVLs();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to sync' }); }
    finally { setSyncing(false); }
  };

  const handleToggleVisibility = async (folder) => {
    try {
      await axios.put(`/admin/dem/virtual-lists/${folder.id}/visibility`, { show_in_database: !folder.show_in_database });
      setFolders(prev => prev.map(f => f.id === folder.id ? { ...f, show_in_database: !f.show_in_database } : f));
    } catch (error) { setMessage({ type: 'error', text: 'Failed to update visibility' }); }
  };

  const handleOpenFolderDialog = (folder = null) => {
    if (folder) {
      setEditingFolder(folder);
      const vlIds = folder.maildome_vl_ids ? folder.maildome_vl_ids.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n)) : [];
      setFolderForm({ name: folder.name, selectedVLs: vlIds });
    } else {
      setEditingFolder(null);
      setFolderForm({ name: '', selectedVLs: [] });
    }
    setFolderDialogOpen(true);
  };

  const handleSaveFolder = async () => {
    if (!folderForm.name.trim()) {
      setMessage({ type: 'error', text: 'Folder name is required' });
      return;
    }
    try {
      const vlIdsString = folderForm.selectedVLs.join(',');
      if (editingFolder) {
        await axios.put(`/admin/dem/virtual-lists/${editingFolder.id}`, { name: folderForm.name });
        await axios.put(`/admin/dem/virtual-lists/${editingFolder.id}/maildome`, { maildome_vl_ids: vlIdsString || null });
      } else {
        const res = await axios.post(`/admin/dem/databases/${selectedDb.id}/virtual-lists`, { name: folderForm.name });
        if (vlIdsString) {
          await axios.put(`/admin/dem/virtual-lists/${res.data.data.id}/maildome`, { maildome_vl_ids: vlIdsString });
        }
      }
      setFolderDialogOpen(false);
      fetchFolders();
      setMessage({ type: 'success', text: editingFolder ? 'Folder updated' : 'Folder created' });
    } catch (error) { setMessage({ type: 'error', text: 'Failed to save folder' }); }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"?`)) return;
    try {
      await axios.delete(`/admin/dem/virtual-lists/${folder.id}`);
      fetchFolders();
      setMessage({ type: 'success', text: 'Folder deleted' });
    } catch (error) { setMessage({ type: 'error', text: 'Failed to delete folder' }); }
  };

  const handleToggleVL = (vlId) => {
    setFolderForm(prev => ({
      ...prev,
      selectedVLs: prev.selectedVLs.includes(vlId)
        ? prev.selectedVLs.filter(id => id !== vlId)
        : [...prev.selectedVLs, vlId]
    }));
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;
  }

  // Database list view
  if (!selectedDb) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={700} mb={2}>Virtual Lists</Typography>
        {message.text && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>{message.text}</Alert>}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>Select Database</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Database Name</TableCell>
                    <TableCell>Partner</TableCell>
                    <TableCell>Maildome ID</TableCell>
                    <TableCell align="center">Folders</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {databases.length === 0 ? (
                    <TableRow><TableCell colSpan={4} align="center">No databases found</TableCell></TableRow>
                  ) : (
                    databases.map((db) => (
                      <TableRow key={db.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedDb(db)}>
                        <TableCell><Typography fontWeight={600}>{db.name}</Typography></TableCell>
                        <TableCell>{db.partner_name || '-'}</TableCell>
                        <TableCell>{db.maildome_db_id || <Chip label="Not set" size="small" color="warning" />}</TableCell>
                        <TableCell align="center">{db.virtual_lists?.length || 0}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Database detail view
  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <IconButton onClick={() => setSelectedDb(null)} size="small"><ArrowBack /></IconButton>
        <Typography variant="h5" fontWeight={700}>{selectedDb.name} - Virtual Lists</Typography>
        <Box flex={1} />
        <Button variant="outlined" startIcon={syncing ? <CircularProgress size={16} /> : <Sync />} onClick={handleSync} disabled={syncing || !selectedDb.maildome_db_id} size="small">
          {syncing ? 'Syncing...' : 'Sync from Maildome'}
        </Button>
      </Box>

      {message.text && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>{message.text}</Alert>}

      {/* Folders Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" fontWeight={600}>Folders ({folders.length})</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenFolderDialog()} size="small">New Folder</Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Folder Name</TableCell>
                  <TableCell>VL IDs</TableCell>
                  <TableCell align="center">Show in Database</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {folders.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}>No folders created yet</TableCell></TableRow>
                ) : (
                  folders.map((folder) => (
                    <TableRow key={folder.id} hover>
                      <TableCell><Typography fontWeight={600}>{folder.name}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {folder.maildome_vl_ids || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleToggleVisibility(folder)} color={folder.show_in_database ? 'primary' : 'default'}>
                          {folder.show_in_database ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleOpenFolderDialog(folder)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => handleDeleteFolder(folder)} color="error"><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Maildome VLs Cache Section */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Maildome Virtual Lists ({maildomeVLs.length})
            {maildomeVLs.length > 0 && <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>Last sync: {maildomeVLs[0]?.synced_at ? new Date(maildomeVLs[0].synced_at).toLocaleString() : '-'}</Typography>}
          </Typography>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {maildomeVLs.length === 0 ? (
                  <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3 }}>No VLs synced. Click "Sync from Maildome" to fetch.</TableCell></TableRow>
                ) : (
                  maildomeVLs.map((vl) => (
                    <TableRow key={vl.id} hover>
                      <TableCell><Chip label={vl.maildome_vl_id} size="small" /></TableCell>
                      <TableCell>{vl.name}</TableCell>
                      <TableCell><Typography variant="body2" color="text.secondary">{vl.description || '-'}</Typography></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Folder Dialog */}
      <Dialog open={folderDialogOpen} onClose={() => { setFolderDialogOpen(false); setVlSearch(''); }}  maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editingFolder ? 'Edit Folder' : 'New Folder'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Folder Name"
            value={folderForm.name}
            onChange={(e) => setFolderForm({ ...folderForm, name: e.target.value })}
            placeholder="e.g., Openers, Op+Null"
            sx={{ mt: 2, mb: 3 }}
          />
             <Typography variant="subtitle2" fontWeight={600} gutterBottom>Select Virtual Lists</Typography>
          {maildomeVLs.length === 0 ? (
            <Alert severity="info">No VLs available. Sync from Maildome first.</Alert>
          ) : (
            <>
            <TextField
              size="small"
              placeholder="Search VLs..."
              fullWidth
              value={vlSearch}
              onChange={(e) => setVlSearch(e.target.value)}
              sx={{ mb: 1 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18 }} /></InputAdornment>
              }}
            />
            <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
              {maildomeVLs.filter(vl => vlSearch === '' || vl.name.toLowerCase().includes(vlSearch.toLowerCase()) || String(vl.maildome_vl_id).includes(vlSearch)).map((vl) => (
                <FormControlLabel
                  key={vl.maildome_vl_id}
                  control={
                    <Checkbox
                      checked={folderForm.selectedVLs.includes(vl.maildome_vl_id)}
                      onChange={() => handleToggleVL(vl.maildome_vl_id)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{vl.maildome_vl_id} - {vl.name}</Typography>}
                  sx={{ display: 'block', m: 0 }}
                />
              ))}
            </Box>
            </>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Selected: {folderForm.selectedVLs.length} VLs {folderForm.selectedVLs.length > 0 && `(${folderForm.selectedVLs.join(', ')})`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
           <Button onClick={() => { setFolderDialogOpen(false); setVlSearch(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveFolder}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DemVirtualLists;
